/*LIBRARY*/
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { EditorState, convertFromRaw } from "draft-js";
import Editor from "draft-js-plugins-editor";
import { Helmet } from "react-helmet-async";

/*COMPONENT*/
import Modal from "../../Components/common_modal/Modal";
import FOLDER_INTRO from "../../Components/blog/FOLDER_INTRO";

/*REDUX*/
import {
  set_value_editor,
  get_public_folder_list,
  get_public_doc_list,
  get_blog_object_by_link_public,
} from "../../App/actions/editor_actions";

/*FUNCTION*/
import { get_media_block_renderer } from "../../Components/common/plugins/mediaBlockRenderer";

/*DATA*/
import dict from "../../Assets/data/dict.json";

/*CONST*/
const styleMap = {
  CODE: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
  BOLD: { fontWeight: 700, color: "#000" },
  ITALIC: { fontStyle: "italic", color: "#333" },
  UNDERLINE: { textDecoration: "underline", textDecorationColor: "#000" },
};

const getBlockStyle = (block) => {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    case "center":
      return "RichEditor-center";
    default:
      return null;
  }
};

/* ICONS */
const ICON_MENU_HAMBURGER = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);
const ICON_CLOSE = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const ICON_CHEVRON_DOWN = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);
const ICON_CHEVRON_UP = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);
const ICON_SEARCH = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
const ICON_GLOBE = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);
const ICON_HOME = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const BLOG_LIST_VIEWER = ({
  editor_reducer,
  set_value_editor,
  get_public_folder_list,
  get_public_doc_list,
  get_blog_object_by_link_public,
}) => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  // Read URL first, if empty fallback to localStorage to protect against reload drops
  const [language, setLanguage] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlLang = searchParams.get("language");
    if (urlLang) {
      localStorage.setItem("mapid_docs_lang", urlLang);
      return urlLang;
    }
    return localStorage.getItem("mapid_docs_lang") || "eng";
  });

  useEffect(() => {
    const urlLang = queryParams.get("language");
    if (!urlLang) {
      queryParams.set("language", language);
      navigate({ search: queryParams.toString() }, { replace: true });
    } else if (urlLang !== language) {
      localStorage.setItem("mapid_docs_lang", urlLang);
      setLanguage(urlLang);
    }
  }, [location.search, language, navigate, queryParams]);

  const handle_language_change = (e) => {
    const newLang = e.target.value;

    // Open language switch in a new tab
    const newUrl = `${window.location.origin}${location.pathname}?language=${newLang}`;
    window.open(newUrl, "_blank");

    // Notice we DO NOT change the local state (setLanguage) or navigate
    // here because the change should strictly happen in the new tab.
    // The select box will automatically snap back visually.
  };

  const parent_link = queryParams.get("parent_link");

  // --- SEO HELPER ---
  const getMetaDescription = () => {
    if (!editor_reducer.selected_blog?.editorState)
      return "MAPID Documentation";
    try {
      const content = JSON.parse(editor_reducer.selected_blog.editorState);
      const firstBlock = content.blocks.find((b) => b.text.trim().length > 10);
      return firstBlock
        ? firstBlock.text.substring(0, 155) + "..."
        : "Learn more at MAPID Docs.";
    } catch (e) {
      return "MAPID Documentation - Interactive Mapping Platform";
    }
  };

  const t = (key) => {
    if (!dict[key]) return key;
    return dict[key][language] || dict[key]["eng"];
  };

  const content_scroll_ref = useRef(null);
  const [window_width, set_window_width] = useState(window.innerWidth);
  const [mobile_sidebar_open, set_mobile_sidebar_open] = useState(false);
  const [expanded_folders, set_expanded_folders] = useState({});
  const [editor_state, set_editor_state] = useState(EditorState.createEmpty());
  const [folders, set_folders] = useState([]);
  const [search_query, set_search_query] = useState("");

  const { blog_list, parent_object, selected_blog, modal_image_url } =
    editor_reducer;
  const is_mobile = window_width <= 800;

  useEffect(() => {
    const handle_resize = () => {
      set_window_width(window.innerWidth);
      if (window.innerWidth > 800) set_mobile_sidebar_open(false);
    };
    window.addEventListener("resize", handle_resize);
    return () => window.removeEventListener("resize", handle_resize);
  }, []);

  useEffect(() => {
    if (is_mobile && !slug) set_mobile_sidebar_open(true);
  }, [is_mobile, slug]);

  useEffect(() => {
    if (content_scroll_ref.current) content_scroll_ref.current.scrollTop = 0;
  }, [selected_blog]);

  // Data Fetching based on SLUG
  useEffect(() => {
    if (slug) {
      get_blog_object_by_link_public(slug);
    } else {
      set_value_editor({ key: "selected_blog", value: null });
    }
  }, [slug, get_blog_object_by_link_public, set_value_editor]);

  useEffect(() => {
    get_public_folder_list(language);
    get_public_doc_list(language);
  }, [language, get_public_folder_list, get_public_doc_list]);

  useEffect(() => {
    set_folders(parent_object?.sub_brackets || []);
  }, [parent_object?.sub_brackets]);

  // --- UPDATED: AUTO EXPAND FOLDER ON SLUG LOAD ---
  useEffect(() => {
    if (folders.length > 0) {
      const initial_expanded = {};
      folders.forEach((folder) => {
        const is_current_folder = selected_blog?.sub_bracket === folder.link;
        initial_expanded[folder.link] = is_current_folder;
      });
      set_expanded_folders(initial_expanded);
    }
  }, [folders, selected_blog]);

  useEffect(() => {
    if (selected_blog?.editorState) {
      try {
        const content_state = convertFromRaw(
          JSON.parse(selected_blog.editorState),
        );
        set_editor_state(EditorState.createWithContent(content_state));
      } catch (error) {
        set_editor_state(EditorState.createEmpty());
      }
    } else {
      set_editor_state(EditorState.createEmpty());
    }
  }, [selected_blog]);

  const new_keywords = [
    "new",
    "baru",
    "update",
    "terbaru",
    "terkini",
    "latest",
    "recent",
    "rilis",
    "2025",
  ];
  const escape_regex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const get_filtered_folders = () => {
    if (!search_query) return folders;
    const safe_query = escape_regex(search_query);
    const regex = new RegExp(safe_query, "i");
    const lower_query = search_query.toLowerCase();
    const search_is_new = new_keywords.some((k) => lower_query.includes(k));
    return folders.filter((folder) => {
      const folder_match = regex.test(folder.name);
      const has_matching_docs = blog_list.some((blog) => {
        if (blog.sub_bracket !== folder.link) return false;
        return regex.test(blog.title) || (search_is_new && blog.is_new);
      });
      return folder_match || has_matching_docs;
    });
  };

  const get_filtered_blogs_by_folder = (folder_link) => {
    if (!search_query) {
      return blog_list.filter((blog) => blog.sub_bracket === folder_link);
    }
    const safe_query = escape_regex(search_query);
    const regex = new RegExp(safe_query, "i");
    const lower_query = search_query.toLowerCase();
    const search_is_new = new_keywords.some((k) => lower_query.includes(k));
    return blog_list.filter((blog) => {
      if (blog.sub_bracket !== folder_link) return false;
      return regex.test(blog.title) || (search_is_new && blog.is_new);
    });
  };

  const get_current_folder_name = () => {
    if (!selected_blog || !folders) return "";
    const folder = folders.find((f) => f.link === selected_blog.sub_bracket);
    return folder ? folder.name : "";
  };

  const handle_image_click = useCallback(
    (url) => {
      set_value_editor({ key: "modal_image_url", value: url });
    },
    [set_value_editor],
  );

  const close_image_modal = () =>
    set_value_editor({ key: "modal_image_url", value: "" });

  const custom_block_renderer = useMemo(
    () => get_media_block_renderer(handle_image_click),
    [handle_image_click],
  );

  const handle_folder_toggle = (folder_link) => {
    set_expanded_folders((prev) => ({
      ...prev,
      [folder_link]: !prev[folder_link],
    }));
  };

  const handle_blog_click = (blog) => {
    set_value_editor({ key: "selected_blog", value: blog });
    if (is_mobile) set_mobile_sidebar_open(false);
  };

  const handle_print = () => {
    if (!selected_blog) return;
    const original_title = document.title;
    document.title = selected_blog.title;
    window.print();
    setTimeout(() => {
      document.title = original_title;
    }, 500);
  };

  const get_blogs_by_folder = (folder_link) => {
    return blog_list.filter((blog) => blog.sub_bracket === folder_link);
  };

  const filtered_folders = get_filtered_folders();
  let className = "RichEditor-editor";
  if (
    !editor_state.getCurrentContent().hasText() &&
    editor_state.getCurrentContent().getBlockMap().first().getType() !==
      "unstyled"
  ) {
    className += " RichEditor-hidePlaceholder";
  }

  return (
    <main>
      <Helmet>
        <title>
          {selected_blog
            ? `${selected_blog.title} - MAPID Docs`
            : "MAPID Documentation"}
        </title>
        <meta name="description" content={getMetaDescription()} />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content={selected_blog ? selected_blog.title : "MAPID Docs"}
        />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={
            modal_image_url || "https://mapid.co.id/img/mapid_logo_black.png"
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={selected_blog ? selected_blog.title : "MAPID Docs"}
        />
        <meta name="twitter:description" content={getMetaDescription()} />
        <link
          rel="canonical"
          href={window.location.origin + window.location.pathname}
        />
      </Helmet>

      {modal_image_url && (
        <Modal
          modalSize="large"
          isOpen={!!modal_image_url}
          onClose={close_image_modal}
        >
          <div className="doc-modal-wrapper">
            <img
              src={modal_image_url}
              alt="Full View"
              className="doc-modal-img"
            />
          </div>
        </Modal>
      )}

      <nav className="doc-navbar no-print">
        <a href="https://mapid.co.id" className="doc-navbar-brand">
          <img
            src="https://mapid.co.id/img/mapid_logo_black.png"
            alt="MAPID"
            className="doc-navbar-logo"
          />
        </a>
        <div className="doc-navbar-actions">
          <ICON_GLOBE />
          <select
            value={language}
            onChange={handle_language_change}
            className="margin_left"
          >
            <option value="ina">Indonesia</option>
            <option value="eng">English</option>
            <option value="eng_mobile">English (Mobile)</option>
          </select>
        </div>
      </nav>

      {is_mobile && mobile_sidebar_open && (
        <div
          className="doc-mobile-overlay no-print"
          onClick={() => set_mobile_sidebar_open(false)}
        />
      )}

      {is_mobile && (
        <button
          className="doc-mobile-toggle no-print"
          onClick={() => set_mobile_sidebar_open(!mobile_sidebar_open)}
        >
          <ICON_MENU_HAMBURGER />
          <span
            style={{ marginLeft: "8px", fontWeight: "600", fontSize: "14px" }}
          >
            {t("Menu")}
          </span>
        </button>
      )}

      <section
        className={`doc-sidebar no-print ${
          is_mobile
            ? `doc-sidebar-mobile ${mobile_sidebar_open ? "is-open" : ""}`
            : "doc-sidebar-desktop"
        }`}
      >
        <div className="doc-sidebar-header">
          {is_mobile && (
            <div className="doc-mobile-close-container">
              <button
                onClick={() => set_mobile_sidebar_open(false)}
                className="button_small button_white outline_grey"
                style={{ border: "none" }}
              >
                <ICON_CLOSE />
              </button>
            </div>
          )}
          <div className="margin_bottom doc-search-wrapper">
            <input
              type="text"
              placeholder={t("Search docs...")}
              value={search_query}
              onChange={(e) => set_search_query(e.target.value)}
              className="doc-search-input"
            />
            <div className="doc-search-icon">
              <ICON_SEARCH />
            </div>
          </div>
        </div>
        <div className="doc-sidebar-body">
          <Link
            to={`/docs?language=${language}`}
            style={{ display: "block", textDecoration: "none" }}
            onClick={() => {
              set_value_editor({ key: "selected_blog", value: null });
              if (is_mobile) set_mobile_sidebar_open(false);
              set_search_query("");
              const reset_state = {};
              if (folders && folders.length > 0) {
                folders.forEach((folder) => {
                  reset_state[folder.link] = false;
                });
              }
              set_expanded_folders(reset_state);
            }}
          >
            <div className="margin_bottom">
              <div
                className="badge_folder button_white text_black doc-folder-row"
                style={{
                  backgroundColor: !selected_blog
                    ? "rgba(0,0,0,0.05)"
                    : undefined,
                }}
              >
                <div className="doc-folder-info">
                  <div className="doc-folder-icon-circle">
                    <ICON_HOME />
                  </div>
                  <span className="text_left">{t("Overview")}</span>
                </div>
                {/* Hidden button added for layout/height consistency with folders */}
                <button
                  className="button_big doc-chevron-btn"
                  style={{ visibility: "hidden", pointerEvents: "none" }}
                >
                  <ICON_CHEVRON_DOWN />
                </button>
              </div>
            </div>
          </Link>

          <hr className="doc-divider" />

          {filtered_folders.map((folder, idx) => {
            const folder_blogs = get_filtered_blogs_by_folder(folder.link);
            const is_expanded =
              expanded_folders[folder.link] || search_query !== "";
            const has_new_content = folder_blogs.some((blog) => blog.is_new);

            return (
              <div key={idx} className="margin_bottom">
                <div
                  className="badge_folder button_white text_black doc-folder-row"
                  onClick={() => handle_folder_toggle(folder.link)}
                >
                  <div className="doc-folder-info">
                    {folder.icon_url ? (
                      <img
                        src={folder.icon_url}
                        alt=""
                        className="doc-folder-img"
                      />
                    ) : (
                      <div className="doc-folder-icon-circle" />
                    )}
                    <span className="text_left">
                      {folder.name} ({folder_blogs.length})
                    </span>
                    {has_new_content && (
                      <span className="badge_small background_black margin_left">
                        {t("New")}
                      </span>
                    )}
                  </div>
                  <button
                    className="button_big doc-chevron-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handle_folder_toggle(folder.link);
                    }}
                  >
                    {is_expanded ? <ICON_CHEVRON_UP /> : <ICON_CHEVRON_DOWN />}
                  </button>
                </div>
                <div
                  className="doc-accordion-container"
                  style={{
                    gridTemplateRows: is_expanded ? "1fr" : "0fr",
                  }}
                >
                  <div className="doc-accordion-inner">
                    <ul className="doc-list-ul">
                      {folder_blogs.map((doc, i) => (
                        <li key={i}>
                          <div className="two_container_auto full_width">
                            <Link
                              className={`doc-link-item ${
                                selected_blog?._id === doc._id
                                  ? "doc-link-item-active"
                                  : "doc-link-item-inactive"
                              }`}
                              onClick={() => handle_blog_click(doc)}
                              to={`/docs/${doc.link}?language=${language}`}
                            >
                              {doc.title}
                              {doc.is_new && (
                                <span className="badge_small background_black margin_left">
                                  {t("New")}
                                </span>
                              )}
                            </Link>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section
        className={`print-area doc-content-area ${
          is_mobile ? "doc-content-mobile" : "doc-content-desktop"
        }`}
        ref={content_scroll_ref}
      >
        <div
          className={
            is_mobile ? "doc-container-mobile" : "doc-container-desktop"
          }
        >
          {selected_blog ? (
            <div>
              <section className="margin_bottom_extra full_width two_container_auto no-print doc-header-nav">
                <div>
                  <span className="badge_small green_container margin_bottom">
                    {get_current_folder_name()}
                  </span>
                  <span className="badge_small background_white margin_bottom">
                    {" ❯ "}
                  </span>
                  <span className="badge_small background_grey_light margin_bottom margin_right">
                    {selected_blog.title}
                  </span>
                </div>
                <div className="text_right">
                  <button
                    className="button background_grey_light margin_bottom margin_left"
                    onClick={handle_print}
                  >
                    {t("Download")}
                  </button>
                  <Link
                    to={`/docs/download_all?language=${language}`}
                    className="button background_grey_light margin_left"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Download All")}
                  </Link>
                </div>
              </section>

              <div id="printable-content">
                <h1
                  className={`doc-title ${
                    is_mobile ? "doc-title-mobile" : "doc-title-desktop"
                  }`}
                >
                  {selected_blog.title}
                </h1>
                {selected_blog?.editorState && (
                  <main>
                    <section className="RichEditor-root">
                      <div className={className}>
                        <Editor
                          blockRendererFn={custom_block_renderer}
                          blockStyleFn={getBlockStyle}
                          customStyleMap={styleMap}
                          editorState={editor_state}
                          readOnly={true}
                          onChange={() => {}}
                        />
                      </div>
                    </section>
                  </main>
                )}
              </div>
            </div>
          ) : (
            <FOLDER_INTRO
              folders={folders}
              parent_link={parent_link}
              get_blogs_by_folder={get_blogs_by_folder}
              handle_blog_click={handle_blog_click}
              set_expanded_folders={set_expanded_folders}
              generate_link_url={(doc) =>
                `/docs/${doc.link}?language=${language}`
              }
            />
          )}
        </div>
      </section>
    </main>
  );
};

const mapStateToProps = (state) => ({ editor_reducer: state.editor_reducer });
export default connect(mapStateToProps, {
  set_value_editor,
  get_public_folder_list,
  get_public_doc_list,
  get_blog_object_by_link_public,
})(BLOG_LIST_VIEWER);
